/**
 * Replay Protection
 *
 * Prevents replay attacks by tracking message sequence numbers per sender.
 * Each message includes a monotonically increasing sequence number that must
 * be higher than any previously seen from that sender.
 */

// In-memory tracking of highest seen sequence per sender per room
const seenSequences = new Map<string, Map<string, number>>()

/**
 * Get the room's sequence map, creating if needed
 */
function getOrCreateRoomMap(roomId: string): Map<string, number> {
  let roomMap = seenSequences.get(roomId)
  if (!roomMap) {
    roomMap = new Map()
    seenSequences.set(roomId, roomMap)
  }
  return roomMap
}

/**
 * Get the next sequence number for a sender
 * This should be called when creating a new message
 */
export function getNextSequence(roomId: string, senderId: string): number {
  const roomMap = getOrCreateRoomMap(roomId)
  const current = roomMap.get(senderId) ?? 0
  const next = current + 1
  roomMap.set(senderId, next)
  return next
}

/**
 * Check if a message sequence is valid (not a replay)
 * Returns true if the sequence is valid (higher than seen), false if replay
 */
export function isValidSequence(
  roomId: string,
  senderId: string,
  sequence: number
): boolean {
  const roomMap = getOrCreateRoomMap(roomId)
  const lastSeen = roomMap.get(senderId) ?? 0

  // Sequence must be strictly greater than last seen
  if (sequence <= lastSeen) {
    console.warn(
      `[ReplayProtection] Replay detected! Sender: ${senderId}, seq: ${sequence}, lastSeen: ${lastSeen}`
    )
    return false
  }

  return true
}

/**
 * Record a sequence as seen after validation
 * Only call this after verifying the message signature!
 */
export function recordSequence(
  roomId: string,
  senderId: string,
  sequence: number
): void {
  const roomMap = getOrCreateRoomMap(roomId)
  const lastSeen = roomMap.get(senderId) ?? 0

  // Only update if this sequence is higher
  if (sequence > lastSeen) {
    roomMap.set(senderId, sequence)
  }
}

/**
 * Check and record a sequence in one operation
 * Returns true if valid and recorded, false if replay
 */
export function checkAndRecordSequence(
  roomId: string,
  senderId: string,
  sequence: number
): boolean {
  if (!isValidSequence(roomId, senderId, sequence)) {
    return false
  }
  recordSequence(roomId, senderId, sequence)
  return true
}

/**
 * Get the current sequence number for a sender (for debugging)
 */
export function getCurrentSequence(roomId: string, senderId: string): number {
  const roomMap = seenSequences.get(roomId)
  return roomMap?.get(senderId) ?? 0
}

/**
 * Clear all sequence tracking for a room (when leaving)
 */
export function clearRoomSequences(roomId: string): void {
  seenSequences.delete(roomId)
}

/**
 * Initialize sequence tracking from existing messages
 * Call this when joining a room to catch up with existing state
 */
export function initializeFromMessages(
  roomId: string,
  messages: Array<{ senderId: string; sequence?: number }>
): void {
  const roomMap = getOrCreateRoomMap(roomId)

  for (const msg of messages) {
    if (msg.sequence !== undefined) {
      const current = roomMap.get(msg.senderId) ?? 0
      if (msg.sequence > current) {
        roomMap.set(msg.senderId, msg.sequence)
      }
    }
  }
}

/**
 * Timestamp-based freshness check
 * Messages older than maxAgeMs are considered stale
 */
export function isMessageFresh(
  timestamp: number,
  maxAgeMs: number = 5 * 60 * 1000 // 5 minutes default
): boolean {
  const now = Date.now()
  const age = now - timestamp

  // Message from the future is suspicious
  if (age < -30000) {
    // 30 seconds tolerance for clock skew
    console.warn(
      `[ReplayProtection] Message from future! Timestamp: ${timestamp}, now: ${now}`
    )
    return false
  }

  // Message too old
  if (age > maxAgeMs) {
    console.warn(
      `[ReplayProtection] Stale message! Age: ${age}ms, max: ${maxAgeMs}ms`
    )
    return false
  }

  return true
}
